import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';

// Create the context with default values
export const ChessAnalysisContext = createContext({
  currentPosition: null,
  setCurrentPosition: () => { },
  isAnalyzing: false,
  setIsAnalyzing: () => { },
  analysisResults: null,
  setAnalysisResults: () => { },
  engineReady: false,
  setEngineReady: () => { },
  shouldRefreshUI: false,
  setShouldRefreshUI: () => { },
  updatePositionSilently: () => { },
  checkForExistingAnalysis: () => { },
  positionsWithAnalysis: {},
});

// Helper function to check if a move has stockfish analysis data
const doesMoveHaveAnalysis = (moveData) => {
  return !!(moveData?.analysis?.pvs || moveData?.stockfish);
};

// Helper function to get the property where stockfish data is stored (different in AddGame vs ViewGame)
const getStockfishData = (moveData) => {
  return moveData?.analysis || moveData?.stockfish || null;
};

// Provider component to wrap the application with
export const ChessAnalysisProvider = ({ children }) => {
  const [currentPosition, setCurrentPosition] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [engineReady, setEngineReady] = useState(false);
  const [shouldRefreshUI, setShouldRefreshUI] = useState(false);

  // Track positions with known analysis (to avoid recalculation)
  const [positionsWithAnalysis, setPositionsWithAnalysis] = useState({});

  // Refs to track last processed values and prevent redundant calls
  const lastPositionRef = useRef(null);
  const lastCheckedMoveRef = useRef(null);

  // Log changes to currentPosition for debugging
  useEffect(() => {
    if (currentPosition && currentPosition !== lastPositionRef.current) {
      
      
      lastPositionRef.current = currentPosition;
    }
  }, [currentPosition, positionsWithAnalysis]);

  // Function to check if a move has existing analysis and populate global window object
  // Use useCallback to memoize this function
  const checkForExistingAnalysis = useCallback((moveData, fen) => {
    if (!moveData || !fen) return false;

    // Create a unique key for this move data to prevent duplicate processing
    const moveKey = `${fen}_${moveData.notation || ''}_${moveData.moveNum || ''}`;

    // Skip if we've already processed this exact move data
    if (lastCheckedMoveRef.current === moveKey) {
      
      return true; // Return true to indicate we have data (even if we're skipping re-processing)
    }

    lastCheckedMoveRef.current = moveKey;

    // Check if this move already has stockfish analysis
    if (doesMoveHaveAnalysis(moveData)) {
      

      // Get the stockfish data (from either analysis or stockfish property)
      const stockfishData = getStockfishData(moveData);

      if (stockfishData) {
        // Update global window object with existing data
        window.moveClassificationData = {
          classification: stockfishData.classification || null,
          positionFEN: fen,
          timestamp: Date.now()
        };

        // Store analysis in global window for AddGame to access
        window.currentAnalysisCache = stockfishData;
        

        // Store this position in our mapping to avoid recalculation
        setPositionsWithAnalysis(prev => ({
          ...prev,
          [fen]: true
        }));

        
        
        return true;
      }
    }

    return false;
  }, []);

  // Function to update position without triggering UI refresh
  // Use useCallback to memoize this function
  const updatePositionSilently = useCallback((position) => {
    // Skip redundant updates for the same position
    if (position === currentPosition) {
      return;
    }

    // Update position without setting shouldRefreshUI to true
    console.trace('ChessAnalysisContext: Silent position update', Date.now(), {
      position,
      hasExistingAnalysis: positionsWithAnalysis[position] ? true : false,
      windowHasData: window.moveClassificationData?.positionFEN === position
    });
    setCurrentPosition(position);
  }, [currentPosition, positionsWithAnalysis]);

  // Create the value object to be provided to consumers
  const contextValue = {
    currentPosition,
    setCurrentPosition,
    isAnalyzing,
    setIsAnalyzing,
    analysisResults,
    setAnalysisResults,
    engineReady,
    setEngineReady,
    shouldRefreshUI,
    setShouldRefreshUI,
    updatePositionSilently,
    checkForExistingAnalysis,
    positionsWithAnalysis,
  };

  return (
    <ChessAnalysisContext.Provider value={contextValue}>
      {children}
    </ChessAnalysisContext.Provider>
  );
};

// Custom hook for using the chess analysis context
export const useChessAnalysis = () => useContext(ChessAnalysisContext); 